import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/baseapi"; // axios instance เดิมของโปรเจกต์ (baseURL ชี้ไป /api และแนบ token ให้แล้ว)

/* =====================================================================
 * ระบบปฏิทินคนไข้ (กายภาพ / พยาบาล / โภชนาการ) — เชื่อมกับ API senizens_api (ผ่าน ../api/baseapi)
 *
 * ใช้ endpoint เดิม:
 *   GET   /api/service-registrations?patientType=AN   รายชื่อคนไข้ AN ที่ยังไม่ discharge (ห้อง/ชื่อ/วันเข้าพัก)
 *   GET   /api/service-registrations/:id              ดึง contract_end_date (วันครบกำหนดเข้าพัก)
 *
 * endpoint ของปฏิทิน (ตาราง calendar_events — ไฟล์ calendarEventsController.js / calendarEvents.js):
 *   GET    /api/calendar-events                 นัดทั้งหมด (นัด F/u / ออกนอกศูนย์ / อื่นๆ)
 *   POST   /api/calendar-events                 เพิ่มนัด
 *   PUT    /api/calendar-events/:id             แก้ไขนัด
 *   PATCH  /api/calendar-events/:id/return      บันทึกว่ากลับถึงศูนย์แล้ว
 *   DELETE /api/calendar-events/:id             ลบนัด
 *
 * ฝั่ง backend อื่นที่ยังต้องมี (ถ้ายังไม่มี ระบบจะเก็บไว้ในเครื่องนี้ชั่วคราว):
 *   /api/patient-tubes (CRUD)      ข้อมูลสายที่ต้องเปลี่ยน
 *   /api/calendar-settings         ตั้งค่าแจ้งเตือนล่วงหน้าของแต่ละแผนก
 * ===================================================================== */

/** เรียก API ผ่าน axios instance เดิม คืนค่า response.data ({success, data, ...}) */
async function http(method, path, body) {
  try {
    const res = await api.request({ method, url: path, data: body });
    const json = res.data;
    if (json && json.success === false) {
      const err = new Error(json.error || json.message || "เกิดข้อผิดพลาด");
      err.status = res.status;
      throw err;
    }
    return json;
  } catch (e) {
    if (e.response) {
      const d = e.response.data || {};
      const err = new Error(d.error || d.message || `HTTP ${e.response.status}`);
      err.status = e.response.status;
      throw err;
    }
    throw e;
  }
}

/** ทำงานแบบจำกัดจำนวนพร้อมกัน เพื่อไม่ให้ยิง API รัวเกินไป */
async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let i = 0;
  const worker = async () => {
    while (i < items.length) {
      const k = i++;
      out[k] = await fn(items[k], k);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

/* ------------------------------ Constants ------------------------------ */
const ALL_DEPTS = ["physio", "nursing", "nutrition"];
const DEPT_LABEL = { physio: "กายภาพ", nursing: "พยาบาล", nutrition: "โภชนาการ" };
const KIND_LABEL = {
  followup: "นัดตรวจ F/u", outing: "ออกนอกศูนย์", tube: "ครบเปลี่ยนสาย",
  admit: "เข้าพัก", discharge: "ครบกำหนด/กลับ", other: "อื่นๆ",
};
const KIND_STYLE = {
  followup: "bg-rose-100 border-rose-400 text-rose-900",
  outing: "bg-teal-100 border-teal-400 text-teal-900",
  tube: "bg-amber-100 border-amber-400 text-amber-900",
  admit: "bg-sky-100 border-sky-400 text-sky-900",
  discharge: "bg-violet-100 border-violet-400 text-violet-900",
  other: "bg-stone-100 border-stone-400 text-stone-800",
};
const MEALS = [
  { key: "b", label: "เช้า", time: "07:30" },
  { key: "l", label: "เที่ยง", time: "12:00" },
  { key: "d", label: "เย็น", time: "17:30" },
];
const TUBE_PRESETS = [
  { type: "สายสวนปัสสาวะ (Foley)", days: 30 },
  { type: "สายให้อาหารทางจมูก (NG tube)", days: 30 },
  { type: "ท่อเจาะคอ (Tracheostomy)", days: 30 },
  { type: "สายให้อาหารทางหน้าท้อง (PEG)", days: 90 },
  { type: "อื่นๆ", days: 30 },
];
const DOW = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];
const MONTHS = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];

/* ------------------------------ Date helpers ------------------------------ */
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (s, n) => { const d = parseISO(s); d.setDate(d.getDate() + n); return toISO(d); };
const diffDays = (a, b) => Math.round((parseISO(b).getTime() - parseISO(a).getTime()) / 86400000);
const fmtShort = (s) => { const d = parseISO(s); return `${DOW[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`; };
const todayISO = () => toISO(new Date());
/** แปลงค่าวันที่จาก API (DATE/TIMESTAMP/ISO) เป็น YYYY-MM-DD ตามเวลาท้องถิ่น */
const toDay = (v) => {
  if (!v) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : toISO(d);
};
/** เวลาจาก API -> HH:MM (กันกรณีได้ HH:MM:SS) */
const toTime = (v) => (v ? String(v).slice(0, 5) : "");

/* ------------------------------ Mapping: API <-> UI ------------------------------ */
function toPatient(r) {
  return {
    id: String(r.registration_id ?? r.id),
    patientId: r.patient_id,
    hn: r.hn || "",
    room: r.room_number || "-",
    name: r.patient_name || [r.prename, r.first_name, r.last_name].filter(Boolean).join(" ") || "ไม่ทราบชื่อ",
    admitDate: toDay(r.registration_date),
    endDate: toDay(r.contract_end_date || r.end_date),
  };
}

/** แถวจากตาราง calendar_events -> appt ที่ UI ใช้ */
function fromEvent(r) {
  const date = toDay(r.date_out);
  return {
    id: String(r.id),
    patientId: String(r.registration_id),
    kind: r.kind || "followup",
    title: r.title || "",
    place: r.place || "",
    date,
    timeOut: toTime(r.time_out),
    dateBack: toDay(r.actual_return_date) || toDay(r.date_back) || date,
    timeBack: toTime(r.time_back),
    note: r.note || "",
    mealNote: r.meal_note || "",
    status: r.status || "scheduled",
  };
}

/** appt ที่ UI ใช้ -> body ที่ส่งเข้า calendar_events */
function toPayload(a) {
  return {
    registration_id: Number(a.patientId),
    kind: a.kind,
    title: a.title,
    place: a.place,
    date_out: a.date,
    time_out: a.timeOut || null,
    date_back: a.dateBack || a.date,
    time_back: a.timeBack || null,
    note: a.note,
    meal_note: a.mealNote,
  };
}

/* ------------------------------ Data loading ------------------------------ */
async function fetchRegistrations() {
  const [reg, ev] = await Promise.all([
    http("GET", "/service-registrations?patientType=AN&limit=500"),
    http("GET", "/calendar-events"),
  ]);
  const patients = (reg.data || []).map(toPatient);
  const ids = new Set(patients.map((p) => p.id));
  const appts = (ev.data || []).map(fromEvent).filter((a) => a.date && ids.has(a.patientId));

  // วันครบกำหนดเข้าพัก อยู่ใน contract ของรายละเอียด registration
  await mapLimit(patients.filter((p) => !p.endDate), 6, async (p) => {
    try {
      const d = await http("GET", `/service-registrations/${p.id}`);
      p.endDate = toDay(d.data && d.data.contract && d.data.contract.contract_end_date);
    } catch { /* ไม่มี contract -> ถือว่ายังไม่กำหนดวันออก */ }
  });
  return { patients, appts };
}

/* ------------------------------ Tube storage (API + local fallback) ------------------------------ */
const LOCAL_TUBES_KEY = "patient-calendar-tubes-v1";
let tubeMode = "api";
const readLocalTubes = () => { try { return JSON.parse(localStorage.getItem(LOCAL_TUBES_KEY) || "[]"); } catch { return []; } };
const writeLocalTubes = (l) => { try { localStorage.setItem(LOCAL_TUBES_KEY, JSON.stringify(l)); } catch { /* ignore */ } };

async function tubeLoad() {
  try {
    const r = await http("GET", "/patient-tubes");
    tubeMode = "api";
    return (r.data || []).map((t) => ({
      id: String(t.id), patientId: String(t.registration_id), type: t.tube_type,
      lastChanged: toDay(t.last_changed), intervalDays: Number(t.interval_days) || 30,
    }));
  } catch {
    tubeMode = "local";
    return readLocalTubes();
  }
}
async function tubeSave(t) {
  if (tubeMode === "api") {
    const body = { registration_id: Number(t.patientId), tube_type: t.type, last_changed: t.lastChanged, interval_days: t.intervalDays };
    return t.id ? http("PUT", `/patient-tubes/${t.id}`, body) : http("POST", "/patient-tubes", body);
  }
  const list = readLocalTubes();
  if (t.id) writeLocalTubes(list.map((x) => (x.id === t.id ? t : x)));
  else writeLocalTubes([...list, { ...t, id: Math.random().toString(36).slice(2, 9) }]);
  return null;
}
async function tubeRemove(id) {
  if (tubeMode === "api") return http("DELETE", `/patient-tubes/${id}`);
  writeLocalTubes(readLocalTubes().filter((x) => x.id !== id));
  return null;
}

/* ------------------------------ Notification settings (API + local fallback) ------------------------------ */
// จำนวนวันที่แจ้งเตือนล่วงหน้าของแต่ละแผนก (ปรับได้ในแท็บ "แจ้งเตือน")
const DEFAULT_LEAD = { physio: 30, nursing: 1, nutrition: 7 };
const LOCAL_SETTINGS_KEY = "patient-calendar-settings-v1";
const ACK_KEY = "patient-calendar-ack-v1";
const MYDEPT_KEY = "patient-calendar-mydept-v1";
const clampDays = (v, d) => { const n = Math.round(Number(v)); return Number.isFinite(n) ? Math.min(365, Math.max(0, n)) : d; };
const normLead = (o) => ({
  physio: clampDays(o && o.physio, DEFAULT_LEAD.physio),
  nursing: clampDays(o && o.nursing, DEFAULT_LEAD.nursing),
  nutrition: clampDays(o && o.nutrition, DEFAULT_LEAD.nutrition),
});
let settingsMode = "api";
async function settingsLoad() {
  try {
    const r = await http("GET", "/calendar-settings");
    settingsMode = "api";
    return normLead(r.data && r.data.lead_days);
  } catch {
    settingsMode = "local";
    try { return normLead(JSON.parse(localStorage.getItem(LOCAL_SETTINGS_KEY) || "null")); } catch { return { ...DEFAULT_LEAD }; }
  }
}
async function settingsSave(lead) {
  if (settingsMode === "api") return http("PUT", "/calendar-settings", { lead_days: lead });
  try { localStorage.setItem(LOCAL_SETTINGS_KEY, JSON.stringify(lead)); } catch { /* ignore */ }
  return null;
}
// "รับทราบแล้ว" เก็บในเบราว์เซอร์ของผู้ใช้แต่ละคน
const readAcked = () => { try { return new Set(JSON.parse(localStorage.getItem(ACK_KEY) || "[]")); } catch { return new Set(); } };
const writeAcked = (set) => { try { localStorage.setItem(ACK_KEY, JSON.stringify([...set])); } catch { /* ignore */ } };

/* ------------------------------ Business logic ------------------------------ */
const nextTubeDue = (t) => addDays(t.lastChanged, t.intervalDays);
const isPresent = (p, d) => !!p.admitDate && p.admitDate <= d && (!p.endDate || d <= p.endDate);

function segment(a, d) {
  const back = a.dateBack || a.date;
  if (d < a.date || d > back) return null;
  return { from: d === a.date ? a.timeOut || "00:00" : "00:00", to: d === back ? a.timeBack || "23:59" : "24:00" };
}

/** มื้ออาหารที่ต้องงด คำนวณจากเวลาออก–กลับ (ถ้ายังไม่ระบุเวลาออก จะไม่คำนวณ) */
function mealsOut(a) {
  const res = [];
  if (!a.date || !a.timeOut) return res;
  const back = a.dateBack || a.date;
  const start = `${a.date}T${a.timeOut}`;
  const end = `${back}T${a.timeBack || "23:59"}`;
  for (let d = a.date; d <= back; d = addDays(d, 1)) {
    for (const m of MEALS) {
      const t = `${d}T${m.time}`;
      if (t >= start && t <= end) res.push({ date: d, meal: m.key, label: m.label });
    }
  }
  return res;
}

function buildItems({ patients, appts, hideWho = false }) {
  const out = [];
  const pm = new Map(patients.map((p) => [p.id, p]));
  for (const p of patients) {
    const who = hideWho ? "" : `${p.room} ${p.name}`;
    if (p.admitDate) out.push({ key: `admit-${p.id}`, date: p.admitDate, time: "", kind: "admit", patientId: p.id, label: `${who} เข้าพัก`, depts: ALL_DEPTS });
    if (p.endDate) out.push({ key: `end-${p.id}`, date: p.endDate, time: "", kind: "discharge", patientId: p.id, label: `${who} ครบกำหนดเข้าพัก`, depts: ALL_DEPTS });
    for (const t of p.tubes) {
      out.push({ key: `tube-${t.id}`, date: nextTubeDue(t), time: "", kind: "tube", patientId: p.id, label: `${who} ครบเปลี่ยน ${t.type}`, depts: ["nursing"] });
    }
  }
  for (const a of appts) {
    const p = pm.get(a.patientId);
    if (!p) continue;
    const who = hideWho ? "" : `${p.room} ${p.name}`;
    const place = a.place ? ` @${a.place}` : "";
    const done = a.status === "returned" ? " (กลับแล้ว)" : "";
    out.push({
      key: `appt-${a.id}`, date: a.date, time: a.timeOut, kind: a.kind, patientId: p.id,
      label: `${who} ${a.title}${place}${a.timeOut ? ` (ออก ${a.timeOut})` : ""}${done}`, depts: ALL_DEPTS, apptId: a.id,
    });
    if (a.dateBack && a.dateBack !== a.date) {
      out.push({ key: `back-${a.id}`, date: a.dateBack, time: a.timeBack, kind: a.kind, patientId: p.id, label: `${who} กลับถึงศูนย์${a.timeBack ? " " + a.timeBack : ""}`, depts: ALL_DEPTS, apptId: a.id });
    }
  }
  return out
    .map((it) => (hideWho ? { ...it, label: it.label.trim() } : it))
    .sort((x, y) => (x.date + x.time).localeCompare(y.date + y.time));
}

/** สร้างรายการแจ้งเตือนของแต่ละแผนก ตามจำนวนวันล่วงหน้า (lead) ที่ตั้งไว้ */
function buildNotifications(patients, appts, lead, today) {
  const out = { physio: [], nursing: [], nutrition: [] };
  const pm = new Map(patients.map((p) => [p.id, p]));
  const add = (dept, n, allowOverdue = false) => {
    const left = diffDays(today, n.date);
    if ((left < 0 && !allowOverdue) || left > lead[dept]) return;
    out[dept].push({ ...n, daysLeft: left });
  };

  for (const a of appts) {
    if (a.status === "returned") continue;
    const p = pm.get(a.patientId);
    if (!p) continue;
    const who = `${p.room} ${p.name}`;
    const where = `${a.title}${a.place ? ` @${a.place}` : ""}`;
    const when = `ออก ${fmtShort(a.date)}${a.timeOut ? " " + a.timeOut : ""} → กลับ ${fmtShort(a.dateBack || a.date)}${a.timeBack ? " " + a.timeBack : ""}`;
    const sig = `${a.date}|${a.timeOut}|${a.dateBack}|${a.timeBack}`;
    const base = { date: a.date, kind: a.kind, patientId: p.id, apptId: a.id, title: `${who} จะไม่อยู่ศูนย์` };

    add("physio", { ...base, key: `appt-${a.id}|physio|${sig}`, detail: `${where} · ${when} — เว้นคิวกายภาพช่วงนี้` });
    add("nursing", { ...base, key: `appt-${a.id}|nursing|${sig}`, detail: `${where} · ${when}${a.note ? ` · ${a.note}` : ""}` });

    const byDay = {};
    mealsOut(a).forEach((m) => { byDay[m.date] = [...(byDay[m.date] || []), m.label]; });
    const mealText = Object.entries(byDay).map(([d, l]) => `${fmtShort(d)} ${l.join("/")}`).join(", ");
    add("nutrition", {
      ...base, key: `appt-${a.id}|nutrition|${sig}|${a.mealNote}`,
      detail: `${where} · ${when} · ${a.timeOut ? (mealText ? `งดอาหาร: ${mealText}` : "ไม่มีมื้อตรงช่วงที่ไม่อยู่") : "ไม่ระบุเวลา ตรวจสอบมื้ออาหาร"}${a.mealNote ? ` · ${a.mealNote}` : ""}`,
    });
  }

  for (const p of patients) {
    const who = `${p.room} ${p.name}`;
    if (p.admitDate) {
      const b = { date: p.admitDate, kind: "admit", patientId: p.id, title: `${who} เข้าพักใหม่` };
      add("physio", { ...b, key: `admit-${p.id}|physio|${p.admitDate}`, detail: "เตรียมประเมินแรกรับ/ลงคิวกายภาพ" });
      add("nursing", { ...b, key: `admit-${p.id}|nursing|${p.admitDate}`, detail: "เตรียมห้องและรับผู้ป่วยใหม่" });
      add("nutrition", { ...b, key: `admit-${p.id}|nutrition|${p.admitDate}`, detail: "เตรียมอาหารสำหรับผู้ป่วยใหม่" });
    }
    if (p.endDate) {
      const b = { date: p.endDate, kind: "discharge", patientId: p.id, title: `${who} ครบกำหนดเข้าพัก` };
      add("physio", { ...b, key: `end-${p.id}|physio|${p.endDate}`, detail: "วันสุดท้าย ปิดแผนกายภาพ/สรุปผล" });
      add("nursing", { ...b, key: `end-${p.id}|nursing|${p.endDate}`, detail: "เตรียมจำหน่าย/ต่อสัญญา" });
      add("nutrition", { ...b, key: `end-${p.id}|nutrition|${p.endDate}`, detail: "ปรับจำนวนอาหารหลังครบกำหนด" });
    }
    for (const t of p.tubes) {
      const due = nextTubeDue(t);
      add("nursing", { key: `tube-${t.id}|nursing|${due}`, date: due, kind: "tube", patientId: p.id, title: `${who} ครบเปลี่ยน ${t.type}`, detail: `เปลี่ยนล่าสุด ${fmtShort(t.lastChanged)} · รอบ ${t.intervalDays} วัน` }, true);
    }
  }
  Object.keys(out).forEach((k) => out[k].sort((x, y) => x.date.localeCompare(y.date)));
  return out;
}

/* ------------------------------ Small UI parts ------------------------------ */
const inputCls = "w-full rounded-md border border-stone-300 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600";
const btnPrimary = "rounded-md bg-emerald-700 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-1 disabled:opacity-40";
const btnGhost = "rounded-md border border-stone-300 bg-white px-3 py-1.5 text-sm text-stone-700 hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-emerald-600";

function Field({ label, children, className = "" }) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="mb-1 block text-stone-600">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onMouseDown={onClose}>
      <div className="mt-8 w-full max-w-2xl rounded-lg bg-white shadow-xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} aria-label="ปิด" className="rounded px-2 text-xl text-stone-500 hover:bg-stone-100">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Chip({ kind, children, onClick }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      className={`block w-full truncate rounded border px-1.5 py-0.5 text-left text-xs leading-snug ${KIND_STYLE[kind]} hover:brightness-95`}>
      {children}
    </button>
  );
}

/* ------------------------------ Patient picker (พิมพ์ค้นหาห้อง/ชื่อ/HN ได้) ------------------------------ */
function PatientPicker({ patients, value, onChange, allLabel, placeholder = "พิมพ์ห้องหรือชื่อ...", disabled = false, ariaLabel = "เลือกคนไข้" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");

  const selected = patients.find((p) => p.id === value);
  const label = selected ? `${selected.room} ${selected.name}` : value === "all" && allLabel ? allLabel : "";

  const list = useMemo(() => {
    const n = norm(q);
    const found = n ? patients.filter((p) => norm(`${p.room}${p.name}${p.hn}`).includes(n)) : patients;
    return allLabel && !n ? [{ id: "all", all: true, name: allLabel }, ...found] : found;
  }, [patients, q, allLabel]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { setHi(0); }, [q]);

  const pick = (it) => { onChange(it.all ? "all" : it.id); setOpen(false); setQ(""); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { if (open && list[hi]) { e.preventDefault(); pick(list[hi]); } }
    else if (e.key === "Escape") { setOpen(false); setQ(""); }
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        role="combobox" aria-expanded={open} aria-label={ariaLabel} autoComplete="off" disabled={disabled}
        className={`${inputCls} pr-7`} placeholder={label || placeholder}
        value={open ? q : label}
        onFocus={(e) => { setOpen(true); setQ(""); e.target.select(); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={onKey}
      />
      {value && value !== "all" && !disabled && allLabel && (
        <button type="button" aria-label="ล้างการเลือก" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded px-1 text-stone-400 hover:text-stone-700"
          onMouseDown={(e) => { e.preventDefault(); onChange("all"); setQ(""); }}>×</button>
      )}
      {open && (
        <ul role="listbox" className="absolute left-0 right-0 z-[60] mt-1 max-h-64 min-w-[14rem] overflow-auto rounded-md border border-stone-300 bg-white py-1 text-sm shadow-lg">
          {list.length === 0 && <li className="px-3 py-2 text-stone-500">ไม่พบคนไข้ที่ตรงกับ "{q}"</li>}
          {list.map((it, i) => (
            <li key={it.id} role="option" aria-selected={it.id === value || (it.all && value === "all")}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }} onMouseEnter={() => setHi(i)}
              className={`cursor-pointer px-3 py-1.5 ${i === hi ? "bg-emerald-50" : ""} ${it.id === value || (it.all && value === "all") ? "font-semibold text-emerald-800" : ""}`}>
              {it.all ? it.name : <>{it.room} · {it.name}{it.hn && <span className="ml-2 text-xs text-stone-500">HN {it.hn}</span>}</>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------ Appointment form ------------------------------ */
function ApptForm({ initial, isNew, patients, onSave, onDelete, onReturned, onClose }) {
  const [a, setA] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setA((x) => ({ ...x, [k]: v }));
  const meals = useMemo(() => mealsOut(a), [a]);
  const mealText = useMemo(() => {
    const g = {};
    meals.forEach((m) => { g[m.date] = [...(g[m.date] || []), m.label]; });
    return Object.entries(g).map(([d, l]) => `${fmtShort(d)}: ${l.join(", ")}`);
  }, [meals]);
  const valid = a.patientId && a.date && a.title.trim();
  const returned = a.status === "returned";

  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  return (
    <Modal title={isNew ? "เพิ่มนัดหมาย" : "แก้ไขนัดหมาย"} onClose={onClose}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="text-sm">
          <span className="mb-1 block text-stone-600">คนไข้ (พิมพ์ห้องหรือชื่อเพื่อค้นหา)</span>
          <PatientPicker patients={patients} value={a.patientId} disabled={!isNew} onChange={(v) => set("patientId", v)} />
        </div>
        <Field label="ประเภท">
          <select className={inputCls} value={a.kind} onChange={(e) => set("kind", e.target.value)}>
            <option value="followup">นัดตรวจ F/u</option>
            <option value="outing">ออกนอกศูนย์ (กลับบ้าน/ธุระอื่น)</option>
            <option value="other">อื่นๆ</option>
          </select>
        </Field>
        <Field label="แผนก/เรื่องที่ไปพบ" className="sm:col-span-2">
          <input className={inputCls} value={a.title} onChange={(e) => set("title", e.target.value)} placeholder="เช่น F/u ศัลยกรรมประสาท" />
        </Field>
        <Field label="ไปที่ไหน (รพ./สถานที่)" className="sm:col-span-2">
          <input className={inputCls} value={a.place} onChange={(e) => set("place", e.target.value)} placeholder="เช่น รพ.ธนบุรี 1" />
        </Field>
        <Field label="วันที่ออกจากศูนย์">
          <input type="date" className={inputCls} value={a.date}
            onChange={(e) => { set("date", e.target.value); if (!a.dateBack || a.dateBack < e.target.value) set("dateBack", e.target.value); }} />
        </Field>
        <Field label="เวลาออก">
          <input type="time" className={inputCls} value={a.timeOut} onChange={(e) => set("timeOut", e.target.value)} />
        </Field>
        <Field label="วันที่กลับถึงศูนย์">
          <input type="date" min={a.date} className={inputCls} value={a.dateBack} onChange={(e) => set("dateBack", e.target.value)} />
        </Field>
        <Field label="เวลากลับถึงศูนย์ (โดยประมาณ)">
          <input type="time" className={inputCls} value={a.timeBack} onChange={(e) => set("timeBack", e.target.value)} />
        </Field>
        <Field label="หมายเหตุ (พยาบาล/กายภาพ)" className="sm:col-span-2">
          <input className={inputCls} value={a.note} onChange={(e) => set("note", e.target.value)} placeholder="เช่น เตรียมใบส่งตัว, พกออกซิเจน" />
        </Field>
        <Field label="สื่อสารฝ่ายโภชนาการ" className="sm:col-span-2">
          <input className={inputCls} value={a.mealNote} onChange={(e) => set("mealNote", e.target.value)} placeholder="เช่น ห่ออาหารไปด้วย, เตรียมอาหารรอตอนกลับ" />
        </Field>
      </div>

      <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
        <div className="font-medium text-emerald-900">มื้ออาหารที่ระบบงดให้อัตโนมัติ</div>
        {!a.timeOut ? (
          <div className="text-stone-600">ยังไม่ได้ระบุเวลาออก ระบบจึงยังไม่คำนวณมื้ออาหารให้</div>
        ) : mealText.length === 0 ? (
          <div className="text-stone-600">ไม่มีมื้ออาหารตรงกับช่วงเวลาที่ไม่อยู่ศูนย์</div>
        ) : (
          <ul className="mt-1 text-stone-800">{mealText.map((t) => <li key={t}>{t}</li>)}</ul>
        )}
        <div className="mt-1 text-xs text-stone-500">เวลามื้อ: เช้า 07:30 · เที่ยง 12:00 · เย็น 17:30 (แก้ได้ที่ค่าคงที่ MEALS)</div>
      </div>

      {returned && <div className="mt-3 rounded-md bg-stone-100 px-3 py-2 text-sm text-stone-700">นัดนี้บันทึกว่ากลับถึงศูนย์แล้ว</div>}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          {!isNew && (
            <button className="rounded-md px-3 py-1.5 text-sm text-red-700 hover:bg-red-50" disabled={busy}
              onClick={() => window.confirm("ลบนัดหมายนี้?") && run(onDelete)}>ลบนัดหมาย</button>
          )}
          {!isNew && !returned && (
            <button className={btnGhost} disabled={busy} onClick={() => run(onReturned)}>บันทึกว่ากลับถึงศูนย์แล้ว</button>
          )}
        </div>
        <div className="flex gap-2">
          <button className={btnGhost} onClick={onClose}>ยกเลิก</button>
          <button className={btnPrimary} disabled={!valid || busy} onClick={() => run(() => onSave(a))}>{busy ? "กำลังบันทึก..." : "บันทึก"}</button>
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------ Tube form ------------------------------ */
function TubeForm({ initial, onSave, onClose }) {
  const [t, setT] = useState(initial);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={initial.id ? "แก้ไขสาย" : "เพิ่มสายที่ต้องเปลี่ยน"} onClose={onClose}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="ชนิดสาย" className="sm:col-span-3">
          <select className={inputCls} value={t.type}
            onChange={(e) => {
              const pr = TUBE_PRESETS.find((x) => x.type === e.target.value);
              setT({ ...t, type: e.target.value, ...(pr ? { intervalDays: pr.days } : {}) });
            }}>
            {TUBE_PRESETS.map((x) => <option key={x.type}>{x.type}</option>)}
          </select>
        </Field>
        <Field label="เปลี่ยนล่าสุด" className="sm:col-span-2">
          <input type="date" className={inputCls} value={t.lastChanged} onChange={(e) => setT({ ...t, lastChanged: e.target.value })} />
        </Field>
        <Field label="เปลี่ยนทุกกี่วัน">
          <input type="number" min={1} className={inputCls} value={t.intervalDays}
            onChange={(e) => setT({ ...t, intervalDays: Math.max(1, Number(e.target.value) || 1) })} />
        </Field>
      </div>
      <div className="mt-1 text-xs text-stone-500">ระยะเปลี่ยนสายตั้งต้นเป็นค่าตัวอย่าง ให้ปรับตามแนวปฏิบัติของหน่วยงาน</div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={btnGhost} onClick={onClose}>ยกเลิก</button>
        <button className={btnPrimary} disabled={busy || !t.lastChanged}
          onClick={async () => { setBusy(true); try { await onSave(t); } finally { setBusy(false); } }}>บันทึก</button>
      </div>
    </Modal>
  );
}

/* ------------------------------ Month calendar ------------------------------ */
function MonthCalendar({ items, onAdd, onEdit }) {
  const now = new Date();
  const [cur, setCur] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [sel, setSel] = useState(todayISO());
  const today = todayISO();

  const first = new Date(cur.y, cur.m, 1);
  const dim = new Date(cur.y, cur.m + 1, 0).getDate();
  const weeks = Math.ceil((first.getDay() + dim) / 7);
  const cells = Array.from({ length: weeks * 7 }, (_, i) => toISO(new Date(cur.y, cur.m, 1 - first.getDay() + i)));
  const byDate = useMemo(() => {
    const m = new Map();
    items.forEach((it) => m.set(it.date, [...(m.get(it.date) || []), it]));
    return m;
  }, [items]);
  const move = (n) => { const d = new Date(cur.y, cur.m + n, 1); setCur({ y: d.getFullYear(), m: d.getMonth() }); };
  const selItems = byDate.get(sel) || [];

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button className={btnGhost} onClick={() => move(-1)} aria-label="เดือนก่อนหน้า">‹</button>
        <div className="min-w-[10rem] text-center text-lg font-semibold">{MONTHS[cur.m]} {cur.y}</div>
        <button className={btnGhost} onClick={() => move(1)} aria-label="เดือนถัดไป">›</button>
        <button className={btnGhost} onClick={() => { setCur({ y: now.getFullYear(), m: now.getMonth() }); setSel(today); }}>วันนี้</button>
        <div className="ml-auto flex flex-wrap gap-1.5 text-xs">
          {Object.keys(KIND_LABEL).map((k) => <span key={k} className={`rounded border px-1.5 py-0.5 ${KIND_STYLE[k]}`}>{KIND_LABEL[k]}</span>)}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-7 border-b border-stone-300 text-center text-sm font-medium text-stone-600">
            {DOW.map((d) => <div key={d} className="py-1.5">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 border-l border-stone-200">
            {cells.map((iso) => {
              const inMonth = parseISO(iso).getMonth() === cur.m;
              const list = byDate.get(iso) || [];
              return (
                <div key={iso} onClick={() => setSel(iso)}
                  className={`min-h-[116px] cursor-pointer border-b border-r border-stone-200 p-1 ${inMonth ? "bg-white" : "bg-stone-50"} ${sel === iso ? "ring-2 ring-inset ring-emerald-600" : ""}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-sm ${iso === today ? "bg-emerald-700 font-semibold text-white" : inMonth ? "text-stone-800" : "text-stone-400"}`}>
                      {parseISO(iso).getDate()}
                    </span>
                    <button className="rounded px-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700" aria-label={`เพิ่มนัดวันที่ ${iso}`}
                      onClick={(e) => { e.stopPropagation(); onAdd(iso); }}>+</button>
                  </div>
                  <div className="space-y-0.5">
                    {list.slice(0, 4).map((it) => (
                      <Chip key={it.key} kind={it.kind} onClick={() => (it.apptId ? onEdit(it.apptId) : setSel(iso))}>
                        {it.time && <span className="font-medium">{it.time} </span>}{it.label}
                      </Chip>
                    ))}
                    {list.length > 4 && <div className="px-1 text-xs text-stone-500">+{list.length - 4} รายการ</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-stone-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold">รายการวันที่ {fmtShort(sel)}</h3>
          <button className={btnPrimary} onClick={() => onAdd(sel)}>+ เพิ่มนัดหมาย</button>
        </div>
        {selItems.length === 0 ? (
          <div className="text-sm text-stone-500">วันนี้ไม่มีรายการ</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {selItems.map((it) => (
              <li key={it.key} className="flex items-center gap-3 py-2 text-sm">
                <span className={`shrink-0 rounded border px-1.5 py-0.5 text-xs ${KIND_STYLE[it.kind]}`}>{KIND_LABEL[it.kind]}</span>
                <span className="grow">{it.time && <b>{it.time} </b>}{it.label}</span>
                {it.apptId && <button className="text-emerald-700 hover:underline" onClick={() => onEdit(it.apptId)}>เปิด</button>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ Physio 30-day plan ------------------------------ */
function PhysioPlan({ patients, appts, patientFilter }) {
  const today = todayISO();
  const days = Array.from({ length: 30 }, (_, i) => addDays(today, i));
  const list = patients.filter((p) => patientFilter === "all" || p.id === patientFilter);

  return (
    <div>
      <p className="mb-3 text-sm text-stone-600">
        มองล่วงหน้า 30 วัน เพื่อลงคิวกายภาพ: <span className="rounded bg-emerald-100 px-1.5 text-emerald-900">เขียว</span> = อยู่ศูนย์ทั้งวัน ลงคิวได้,{" "}
        <span className="rounded bg-amber-100 px-1.5 text-amber-900">เหลือง</span> = ออกนอกศูนย์บางช่วง,{" "}
        <span className="rounded bg-sky-100 px-1.5 text-sky-900">ฟ้า</span> = เข้าพักใหม่/ประเมินแรกรับ,{" "}
        <span className="rounded bg-violet-100 px-1.5 text-violet-900">ม่วง</span> = วันสุดท้าย
      </p>
      <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-stone-100 text-left text-stone-600">
            <tr><th className="w-28 px-3 py-2">วัน</th><th className="w-40 px-3 py-2">ลงคิวได้ทั้งวัน</th><th className="px-3 py-2">คนไข้ในศูนย์</th></tr>
          </thead>
          <tbody>
            {days.map((d) => {
              const present = list.filter((p) => isPresent(p, d));
              const info = present.map((p) => {
                const a = appts.find((x) => x.patientId === p.id && segment(x, d));
                return { p, a, seg: a ? segment(a, d) : null };
              });
              const free = info.filter((i) => !i.seg).length;
              const wk = parseISO(d).getDay();
              return (
                <tr key={d} className={`border-t border-stone-100 align-top ${wk === 0 || wk === 6 ? "bg-stone-50" : ""}`}>
                  <td className="px-3 py-2 font-medium">{fmtShort(d)}{d === today && <span className="ml-1 text-xs text-emerald-700">วันนี้</span>}</td>
                  <td className="px-3 py-2"><span className="text-lg font-semibold">{free}</span><span className="text-stone-500"> / {present.length} คน</span></td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1.5">
                      {info.length === 0 && <span className="text-stone-400">-</span>}
                      {info.map(({ p, seg, a }) => {
                        const isNew = p.admitDate === d;
                        const isLast = p.endDate === d;
                        const cls = seg ? "bg-amber-100 border-amber-400 text-amber-900"
                          : isNew ? "bg-sky-100 border-sky-400 text-sky-900"
                          : isLast ? "bg-violet-100 border-violet-400 text-violet-900"
                          : "bg-emerald-100 border-emerald-400 text-emerald-900";
                        return (
                          <span key={p.id} className={`rounded border px-2 py-0.5 text-xs ${cls}`}>
                            {p.room} {p.name}
                            {seg && (a.timeOut ? ` · ออก ${seg.from}–${seg.to === "24:00" ? "ค้างคืน" : seg.to}` : " · ออก (ไม่ระบุเวลา)")}
                            {seg && a.place ? ` (${a.place})` : ""}
                            {!seg && isNew && " · เข้าใหม่"}
                            {!seg && isLast && " · วันสุดท้าย"}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------ Nutrition view ------------------------------ */
function NutritionPlan({ patients, appts, patientFilter }) {
  const today = todayISO();
  const [range, setRange] = useState(14);
  const days = Array.from({ length: range }, (_, i) => addDays(today, i));
  const list = useMemo(() => patients.filter((p) => patientFilter === "all" || p.id === patientFilter), [patients, patientFilter]);

  const cancels = useMemo(() => {
    const pm = new Map(list.map((p) => [p.id, p]));
    const m = new Map();
    for (const a of appts) {
      const p = pm.get(a.patientId);
      if (!p) continue;
      for (const o of mealsOut(a)) {
        const k = `${o.date}|${o.meal}`;
        m.set(k, [...(m.get(k) || []), { patient: p, appt: a }]);
      }
    }
    return m;
  }, [appts, list]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-sm text-stone-600">
        <span>จำนวนมื้อที่ต้องเตรียม = คนไข้ในศูนย์ − คนไข้ที่งดมื้อนั้น (คำนวณจากเวลาออก–กลับของนัดหมาย)</span>
        <select className={`${inputCls} !w-auto`} value={range} onChange={(e) => setRange(Number(e.target.value))}>
          <option value={7}>7 วัน</option><option value={14}>14 วัน</option><option value={30}>30 วัน</option>
        </select>
      </div>
      <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-stone-100 text-left text-stone-600">
            <tr>
              <th className="w-28 px-3 py-2">วัน</th>
              {MEALS.map((m) => <th key={m.key} className="px-3 py-2">มื้อ{m.label} ({m.time})</th>)}
              <th className="px-3 py-2">เข้า/ครบกำหนด</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => {
              const present = list.filter((p) => isPresent(p, d));
              const moves = [
                ...list.filter((p) => p.admitDate === d).map((p) => `เข้าใหม่ ${p.room} ${p.name}`),
                ...list.filter((p) => p.endDate === d).map((p) => `ครบกำหนด ${p.room} ${p.name}`),
              ];
              const noTime = appts.filter((a) => !a.timeOut && a.date <= d && d <= (a.dateBack || a.date) && list.some((p) => p.id === a.patientId));
              return (
                <tr key={d} className="border-t border-stone-100 align-top">
                  <td className="px-3 py-2 font-medium">{fmtShort(d)}{d === today && <span className="ml-1 text-xs text-emerald-700">วันนี้</span>}</td>
                  {MEALS.map((m) => {
                    const c = cancels.get(`${d}|${m.key}`) || [];
                    const prepare = Math.max(0, present.length - c.filter((x) => isPresent(x.patient, d)).length);
                    return (
                      <td key={m.key} className="px-3 py-2">
                        <div><span className="text-lg font-semibold">{prepare}</span><span className="text-stone-500"> ที่</span></div>
                        {c.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {c.map(({ patient, appt }) => (
                              <div key={patient.id + appt.id} className="rounded bg-rose-50 px-1.5 py-0.5 text-xs text-rose-900">
                                งด {patient.room} {patient.name}
                                {appt.mealNote && <span className="text-stone-700"> – {appt.mealNote}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-xs text-stone-700">
                    {moves.map((t) => <div key={t}>{t}</div>)}
                    {noTime.map((a) => {
                      const p = list.find((x) => x.id === a.patientId);
                      return <div key={a.id} className="text-amber-800">ไม่ระบุเวลา: {p.room} {p.name} (ตรวจสอบมื้ออาหาร)</div>;
                    })}
                    {moves.length === 0 && noTime.length === 0 && <span className="text-stone-400">-</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------ Nursing view ------------------------------ */
function NursingPlan({ patients, appts, patientFilter, onTubeDone, onEdit }) {
  const today = todayISO();
  const list = patients.filter((p) => patientFilter === "all" || p.id === patientFilter);
  const rows = list.flatMap((p) => p.tubes.map((t) => ({ p, t, due: nextTubeDue(t) }))).sort((a, b) => a.due.localeCompare(b.due));
  const upcoming = appts
    .filter((a) => a.status !== "returned" && a.date >= today && a.date <= addDays(today, 14) && list.some((p) => p.id === a.patientId))
    .sort((a, b) => (a.date + a.timeOut).localeCompare(b.date + b.timeOut));
  const pm = new Map(patients.map((p) => [p.id, p]));

  return (
    <div className="space-y-5">
      <section>
        <h3 className="mb-2 font-semibold">กำหนดเปลี่ยนสาย</h3>
        <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-stone-100 text-left text-stone-600">
              <tr><th className="px-3 py-2">คนไข้</th><th className="px-3 py-2">สาย</th><th className="px-3 py-2">เปลี่ยนล่าสุด</th><th className="px-3 py-2">ครบกำหนด</th><th className="px-3 py-2">สถานะ</th><th className="px-3 py-2" /></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-stone-500">ยังไม่มีสายที่ติดตาม (เพิ่มได้ที่แท็บ "คนไข้")</td></tr>}
              {rows.map(({ p, t, due }) => {
                const left = diffDays(today, due);
                const badge = left < 0 ? [`เกินกำหนด ${-left} วัน`, "bg-red-100 text-red-800"]
                  : left === 0 ? ["ครบวันนี้", "bg-red-100 text-red-800"]
                  : left <= 7 ? [`อีก ${left} วัน`, "bg-amber-100 text-amber-900"]
                  : [`อีก ${left} วัน`, "bg-emerald-100 text-emerald-900"];
                return (
                  <tr key={t.id} className="border-t border-stone-100">
                    <td className="px-3 py-2">{p.room} {p.name}</td>
                    <td className="px-3 py-2">{t.type}</td>
                    <td className="px-3 py-2">{fmtShort(t.lastChanged)}</td>
                    <td className="px-3 py-2">{fmtShort(due)}</td>
                    <td className="px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-xs ${badge[1]}`}>{badge[0]}</span></td>
                    <td className="px-3 py-2 text-right"><button className={btnGhost} onClick={() => onTubeDone(t)}>เปลี่ยนแล้ววันนี้</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3 className="mb-2 font-semibold">นัดไปตรวจ/ออกนอกศูนย์ใน 14 วันข้างหน้า</h3>
        <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-stone-100 text-left text-stone-600">
              <tr><th className="px-3 py-2">วัน-เวลาออก</th><th className="px-3 py-2">คนไข้</th><th className="px-3 py-2">ไปที่ไหน/เรื่อง</th><th className="px-3 py-2">กลับถึงศูนย์</th><th className="px-3 py-2">หมายเหตุ</th><th className="px-3 py-2" /></tr>
            </thead>
            <tbody>
              {upcoming.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-stone-500">ไม่มีนัดหมาย</td></tr>}
              {upcoming.map((a) => {
                const p = pm.get(a.patientId);
                return (
                  <tr key={a.id} className="border-t border-stone-100">
                    <td className="px-3 py-2">{fmtShort(a.date)} {a.timeOut}</td>
                    <td className="px-3 py-2">{p.room} {p.name}</td>
                    <td className="px-3 py-2">{a.place || "-"} · {a.title}</td>
                    <td className="px-3 py-2">{fmtShort(a.dateBack || a.date)} {a.timeBack}</td>
                    <td className="px-3 py-2">{a.note || "-"}</td>
                    <td className="px-3 py-2 text-right"><button className="text-emerald-700 hover:underline" onClick={() => onEdit(a.id)}>เปิด</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------ Patients tab ------------------------------ */
function PatientsTab({ patients, appts, onViewCalendar, onAddAppt, onAddTube, onEditTube, onDeleteTube, onTubeDone }) {
  const today = todayISO();
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {patients.length === 0 && <div className="text-sm text-stone-500">ไม่พบคนไข้ AN ที่ยังพักอยู่</div>}
      {patients.map((p) => {
        const next = appts.filter((a) => a.patientId === p.id && a.status !== "returned" && a.date >= today).sort((a, b) => a.date.localeCompare(b.date))[0];
        return (
          <div key={p.id} className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="text-lg font-semibold">{p.room} · {p.name}</div>
            <div className="text-sm text-stone-600">
              {p.hn && <>HN {p.hn} · </>}พัก {p.admitDate ? fmtShort(p.admitDate) : "-"} → {p.endDate ? fmtShort(p.endDate) : "ไม่ระบุวันครบกำหนด"}
            </div>
            <div className="mt-2 text-sm">
              นัดถัดไป: {next
                ? <>{fmtShort(next.date)} {next.timeOut} {next.place && `@${next.place}`} → กลับ {fmtShort(next.dateBack)} {next.timeBack}</>
                : <span className="text-stone-400">ไม่มี</span>}
            </div>
            <ul className="mt-2 space-y-1 text-sm">
              {p.tubes.map((t) => (
                <li key={t.id} className="flex items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1">
                  <span className="grow">{t.type} · ครบ {fmtShort(nextTubeDue(t))}</span>
                  <button className="text-emerald-700 hover:underline" onClick={() => onTubeDone(t)}>เปลี่ยนแล้ว</button>
                  <button className="text-stone-600 hover:underline" onClick={() => onEditTube(t)}>แก้ไข</button>
                  <button className="text-red-700 hover:underline" onClick={() => onDeleteTube(t)}>ลบ</button>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              <button className={btnGhost} onClick={() => onViewCalendar(p.id)}>ดูปฏิทิน</button>
              <button className={btnGhost} onClick={() => onAddAppt(p.id)}>+ นัดหมาย</button>
              <button className={btnGhost} onClick={() => onAddTube(p.id)}>+ สาย</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------ Notifications tab ------------------------------ */
const daysLabel = (left) => (left < 0 ? `เกินกำหนด ${-left} วัน` : left === 0 ? "วันนี้" : left === 1 ? "พรุ่งนี้" : `อีก ${left} วัน`);

function NotificationsPanel({ notifs, lead, settingsLocal, onSaveLead, acked, onAck, myDept, setMyDept, onOpenAppt }) {
  const [view, setView] = useState(myDept || "physio");
  const [showDone, setShowDone] = useState(false);
  const [draft, setDraft] = useState(lead);
  const [saved, setSaved] = useState("");
  useEffect(() => { setDraft(lead); }, [lead]);

  const list = notifs[view] || [];
  const unread = list.filter((n) => !acked.has(n.key));
  const done = list.filter((n) => acked.has(n.key));
  const dirty = ALL_DEPTS.some((d) => Number(draft[d]) !== lead[d]);

  const card = (n, isDone) => {
    const urgent = n.daysLeft <= 0;
    const soon = n.daysLeft === 1;
    return (
      <li key={n.key} className={`rounded-lg border p-3 ${isDone ? "border-stone-200 bg-stone-50 opacity-70" : urgent ? "border-red-300 bg-red-50" : soon ? "border-amber-300 bg-amber-50" : "border-stone-200 bg-white"}`}>
        <div className="flex flex-wrap items-start gap-2">
          <span className={`shrink-0 rounded border px-1.5 py-0.5 text-xs ${KIND_STYLE[n.kind]}`}>{KIND_LABEL[n.kind]}</span>
          <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${urgent ? "bg-red-600 text-white" : soon ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-800"}`}>
            {daysLabel(n.daysLeft)} · {fmtShort(n.date)}
          </span>
          <div className="ml-auto flex gap-2 text-sm">
            {n.apptId && <button className="text-emerald-700 hover:underline" onClick={() => onOpenAppt(n.apptId)}>เปิดนัด</button>}
            {!isDone && <button className={btnGhost} onClick={() => onAck([n.key])}>รับทราบ</button>}
          </div>
        </div>
        <div className="mt-1 font-medium">{n.title}</div>
        <div className="text-sm text-stone-700">{n.detail}</div>
      </li>
    );
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <h3 className="mb-1 font-semibold">ตั้งค่าแจ้งเตือนล่วงหน้าของแต่ละแผนก</h3>
        <p className="mb-3 text-sm text-stone-600">ระบบจะแจ้งเมื่อคนไข้จะไม่อยู่ศูนย์ เข้าพัก/ครบกำหนด และครบเปลี่ยนสาย (พยาบาล) ภายในจำนวนวันที่ตั้งไว้ (0 = แจ้งเฉพาะวันนั้น)</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          {ALL_DEPTS.map((d) => (
            <Field key={d} label={`${DEPT_LABEL[d]} ล่วงหน้า (วัน)`}>
              <input type="number" min={0} max={365} className={inputCls} value={draft[d]}
                onChange={(e) => { setSaved(""); setDraft({ ...draft, [d]: e.target.value }); }} />
            </Field>
          ))}
          <div className="flex items-end gap-2">
            <button className={btnPrimary} disabled={!dirty}
              onClick={async () => { const ok = await onSaveLead(normLead(draft)); setSaved(ok ? "บันทึกแล้ว" : ""); }}>บันทึก</button>
            {saved && <span className="pb-1.5 text-sm text-emerald-700">{saved}</span>}
          </div>
        </div>
        {settingsLocal && (
          <div className="mt-2 text-xs text-amber-800">ยังเรียก /api/calendar-settings ไม่ได้ ค่านี้จึงบันทึกเฉพาะเบราว์เซอร์เครื่องนี้ (เพิ่มไฟล์ calendarSettings.js ฝั่ง backend เพื่อให้ทุกเครื่องใช้ค่าเดียวกัน)</div>
        )}
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          {ALL_DEPTS.map((d) => {
            const n = (notifs[d] || []).filter((x) => !acked.has(x.key)).length;
            return (
              <button key={d} onClick={() => setView(d)}
                className={`rounded-full border px-3 py-1 ${view === d ? "border-emerald-700 bg-emerald-700 text-white" : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"}`}>
                {DEPT_LABEL[d]} ({lead[d]} วัน){n > 0 && <span className={`ml-1.5 rounded-full px-1.5 text-xs ${view === d ? "bg-white text-emerald-800" : "bg-red-600 text-white"}`}>{n}</span>}
              </button>
            );
          })}
          <label className="ml-auto flex items-center gap-2 text-stone-600">
            ฉันอยู่ฝ่าย:
            <select className={`${inputCls} !w-auto`} value={myDept} onChange={(e) => { setMyDept(e.target.value); if (e.target.value) setView(e.target.value); }}>
              <option value="">ยังไม่เลือก</option>
              {ALL_DEPTS.map((d) => <option key={d} value={d}>{DEPT_LABEL[d]}</option>)}
            </select>
          </label>
        </div>

        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold">แจ้งเตือนฝ่าย{DEPT_LABEL[view]} · ล่วงหน้า {lead[view]} วัน</h3>
          {unread.length > 1 && <button className={btnGhost} onClick={() => onAck(unread.map((n) => n.key))}>รับทราบทั้งหมด ({unread.length})</button>}
        </div>
        {unread.length === 0 ? (
          <div className="rounded-lg border border-stone-200 bg-white p-4 text-sm text-stone-500">ไม่มีแจ้งเตือนใหม่ในช่วง {lead[view]} วันข้างหน้า</div>
        ) : (
          <ul className="space-y-2">{unread.map((n) => card(n, false))}</ul>
        )}

        {done.length > 0 && (
          <div className="mt-3">
            <button className="text-sm text-stone-600 underline" onClick={() => setShowDone((v) => !v)}>
              {showDone ? "ซ่อน" : "แสดง"}ที่รับทราบแล้ว ({done.length})
            </button>
            {showDone && <ul className="mt-2 space-y-2">{done.map((n) => card(n, true))}</ul>}
          </div>
        )}
        <div className="mt-2 text-xs text-stone-500">สถานะ "รับทราบ" เก็บในเบราว์เซอร์ของแต่ละคน ถ้านัดถูกแก้ไขวัน/เวลา ระบบจะแจ้งเตือนใหม่อีกครั้ง</div>
      </section>
    </div>
  );
}

/* ------------------------------ Single-patient calendar ------------------------------ */
function PatientCalendarTab({ patients, appts, patientId, onSelect, onAddAppt, onEditAppt, onAddTube, onEditTube, onTubeDone }) {
  const today = todayISO();
  const [showPast, setShowPast] = useState(false);
  const p = patients.find((x) => x.id === patientId);
  const mine = useMemo(() => appts.filter((a) => a.patientId === patientId), [appts, patientId]);
  const items = useMemo(() => (p ? buildItems({ patients: [p], appts: mine, hideWho: true }) : []), [p, mine]);

  const backOf = (a) => a.dateBack || a.date;
  const upcoming = mine
    .filter((a) => a.status !== "returned" && backOf(a) >= today)
    .sort((a, b) => (a.date + a.timeOut).localeCompare(b.date + b.timeOut));
  const past = mine
    .filter((a) => !(a.status !== "returned" && backOf(a) >= today))
    .sort((a, b) => (b.date + b.timeOut).localeCompare(a.date + a.timeOut));

  const stateBadge = (a) => {
    if (a.status === "returned") return ["กลับแล้ว", "bg-stone-200 text-stone-700"];
    if (backOf(a) < today) return ["ยังไม่บันทึกว่ากลับ", "bg-red-100 text-red-800"];
    if (a.date <= today) return ["อยู่นอกศูนย์", "bg-amber-100 text-amber-900"];
    const left = diffDays(today, a.date);
    return [left === 1 ? "พรุ่งนี้" : `อีก ${left} วัน`, "bg-emerald-100 text-emerald-900"];
  };

  const apptRow = (a) => {
    const [label, cls] = stateBadge(a);
    return (
      <li key={a.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
        <span className={`shrink-0 rounded border px-1.5 py-0.5 text-xs ${KIND_STYLE[a.kind]}`}>{KIND_LABEL[a.kind]}</span>
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs ${cls}`}>{label}</span>
        <span className="grow">
          <b>{fmtShort(a.date)}{a.timeOut && ` ${a.timeOut}`}</b> · {a.title}{a.place && ` @${a.place}`}
          {backOf(a) !== a.date || a.timeBack ? <span className="text-stone-500"> → กลับ {fmtShort(backOf(a))}{a.timeBack && ` ${a.timeBack}`}</span> : null}
        </span>
        <button className="text-emerald-700 hover:underline" onClick={() => onEditAppt(a.id)}>เปิด</button>
      </li>
    );
  };

  const stay = p && p.endDate ? diffDays(today, p.endDate) : null;

  return (
    <div className="space-y-4">
      <div className="max-w-md">
        <span className="mb-1 block text-sm text-stone-600">เลือกคนไข้ (พิมพ์ห้องหรือชื่อเพื่อค้นหา)</span>
        <PatientPicker patients={patients} value={patientId} onChange={onSelect} ariaLabel="เลือกคนไข้เพื่อดูปฏิทิน" />
      </div>

      {!patientId && (
        <div className="rounded-lg border border-stone-200 bg-white p-6 text-sm text-stone-500">เลือกคนไข้เพื่อดูปฏิทินของคนไข้รายนั้น</div>
      )}
      {patientId && !p && (
        <div className="rounded-lg border border-stone-200 bg-white p-6 text-sm text-stone-500">ไม่พบคนไข้รายนี้ในรายชื่อ AN ที่ยังพักอยู่</div>
      )}

      {p && (
        <>
          <section className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="flex flex-wrap items-start gap-3">
              <div>
                <div className="text-xl font-semibold">{p.room} · {p.name}</div>
                <div className="text-sm text-stone-600">
                  {p.hn && <>HN {p.hn} · </>}พัก {p.admitDate ? fmtShort(p.admitDate) : "-"} → {p.endDate ? fmtShort(p.endDate) : "ไม่ระบุวันครบกำหนด"}
                  {stay !== null && (
                    <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${stay < 0 ? "bg-red-100 text-red-800" : stay <= 7 ? "bg-amber-100 text-amber-900" : "bg-stone-100 text-stone-700"}`}>
                      {stay < 0 ? `เกินกำหนด ${-stay} วัน` : stay === 0 ? "ครบกำหนดวันนี้" : `เหลืออีก ${stay} วัน`}
                    </span>
                  )}
                </div>
              </div>
              <div className="ml-auto flex gap-2">
                <button className={btnPrimary} onClick={() => onAddAppt(today, p.id)}>+ นัดหมาย</button>
                <button className={btnGhost} onClick={() => onAddTube(p.id)}>+ สาย</button>
              </div>
            </div>

            {p.tubes.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm">
                {p.tubes.map((t) => {
                  const due = nextTubeDue(t);
                  const left = diffDays(today, due);
                  const cls = left <= 0 ? "bg-red-100 text-red-800" : left <= 7 ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900";
                  return (
                    <li key={t.id} className="flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1">
                      <span className="grow">{t.type} · ครบ {fmtShort(due)}</span>
                      <span className={`rounded px-1.5 py-0.5 text-xs ${cls}`}>{left < 0 ? `เกินกำหนด ${-left} วัน` : left === 0 ? "ครบวันนี้" : `อีก ${left} วัน`}</span>
                      <button className="text-emerald-700 hover:underline" onClick={() => onTubeDone(t)}>เปลี่ยนแล้ว</button>
                      <button className="text-stone-600 hover:underline" onClick={() => onEditTube(t)}>แก้ไข</button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <MonthCalendar key={p.id} items={items} onAdd={(date) => onAddAppt(date, p.id)} onEdit={onEditAppt} />

          <section className="rounded-lg border border-stone-200 bg-white p-4">
            <h3 className="mb-1 font-semibold">นัดหมายที่กำลังจะถึง</h3>
            {upcoming.length === 0 ? (
              <div className="text-sm text-stone-500">ไม่มีนัดหมายที่กำลังจะถึง</div>
            ) : (
              <ul className="divide-y divide-stone-100">{upcoming.map(apptRow)}</ul>
            )}
            {past.length > 0 && (
              <div className="mt-3">
                <button className="text-sm text-stone-600 underline" onClick={() => setShowPast((v) => !v)}>
                  {showPast ? "ซ่อน" : "แสดง"}ประวัติย้อนหลัง ({past.length})
                </button>
                {showPast && <ul className="mt-1 divide-y divide-stone-100">{past.map(apptRow)}</ul>}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/* ------------------------------ App ------------------------------ */
const TABS = [
  { key: "calendar", label: "ปฏิทินรวม" },
  { key: "patient", label: "ปฏิทินคนไข้" },
  { key: "physio", label: "กายภาพ: แผน 30 วัน" },
  { key: "nursing", label: "พยาบาล: สาย/นัด" },
  { key: "nutrition", label: "โภชนาการ: มื้ออาหาร" },
  { key: "notifications", label: "แจ้งเตือน" },
  { key: "patients", label: "คนไข้" },
];

export default function PatientCalendarApp() {
  const [data, setData] = useState({ patients: [], appts: [] });
  const [tubes, setTubes] = useState([]);
  const [tubeLocal, setTubeLocal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("calendar");
  const [dept, setDept] = useState("all");
  const [patientFilter, setPatientFilter] = useState("all");
  const [calPatient, setCalPatient] = useState("");
  const [apptModal, setApptModal] = useState(null);
  const [tubeModal, setTubeModal] = useState(null);
  const [lead, setLead] = useState({ ...DEFAULT_LEAD });
  const [settingsLocal, setSettingsLocal] = useState(false);
  const [acked, setAcked] = useState(readAcked);
  const [myDept, setMyDeptState] = useState(() => { try { return localStorage.getItem(MYDEPT_KEY) || ""; } catch { return ""; } });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchRegistrations());
      setError("");
    } catch (e) {
      setError(`โหลดข้อมูลไม่สำเร็จ: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  const reloadTubes = useCallback(async () => {
    setTubes(await tubeLoad());
    setTubeLocal(tubeMode === "local");
  }, []);
  const reloadSettings = useCallback(async () => {
    setLead(await settingsLoad());
    setSettingsLocal(settingsMode === "local");
  }, []);
  useEffect(() => { reload(); reloadTubes(); reloadSettings(); }, [reload, reloadTubes, reloadSettings]);

  const setMyDept = (d) => { setMyDeptState(d); try { localStorage.setItem(MYDEPT_KEY, d); } catch { /* ignore */ } };
  const saveLead = async (next) => {
    try { await settingsSave(next); setLead(next); return true; }
    catch (e) { setError(`บันทึกการตั้งค่าไม่สำเร็จ: ${e.message}`); return false; }
  };
  const ackKeys = (keys) => {
    const next = new Set(acked);
    keys.forEach((k) => next.add(k));
    setAcked(next);
    writeAcked(next);
  };

  const patients = useMemo(
    () => data.patients.map((p) => ({ ...p, tubes: tubes.filter((t) => t.patientId === p.id) })),
    [data.patients, tubes]
  );
  const items = useMemo(
    () => buildItems({ patients, appts: data.appts }).filter(
      (it) => (dept === "all" || it.depts.includes(dept)) && (patientFilter === "all" || it.patientId === patientFilter)
    ),
    [patients, data.appts, dept, patientFilter]
  );

  const notifs = useMemo(() => buildNotifications(patients, data.appts, lead, todayISO()), [patients, data.appts, lead]);
  const unreadOf = (d) => notifs[d].filter((n) => !acked.has(n.key)).length;
  const badgeCount = myDept ? unreadOf(myDept) : ALL_DEPTS.reduce((s, d) => s + unreadOf(d), 0);

  const newAppt = (date = todayISO(), patientId = patientFilter === "all" ? "" : patientFilter) => ({
    id: "", patientId, kind: "followup", title: "", place: "", date, timeOut: "08:00", dateBack: date, timeBack: "16:00",
    note: "", mealNote: "", status: "scheduled",
  });
  const openAppt = (id) => {
    const a = data.appts.find((x) => x.id === id);
    if (a) setApptModal({ appt: a, isNew: false });
  };
  const fail = (e, prefix) => setError(`${prefix}: ${e.message}${e.status === 404 || e.status === 405 ? " (ยังไม่มี endpoint นี้ในฝั่ง backend)" : ""}`);

  const saveAppt = async (a, isNew) => {
    try {
      if (isNew) await http("POST", "/calendar-events", toPayload(a));
      else await http("PUT", `/calendar-events/${a.id}`, toPayload(a));
      setApptModal(null);
      await reload();
    } catch (e) { fail(e, "บันทึกนัดหมายไม่สำเร็จ"); }
  };
  const deleteAppt = async (id) => {
    try { await http("DELETE", `/calendar-events/${id}`); setApptModal(null); await reload(); }
    catch (e) { fail(e, "ลบนัดหมายไม่สำเร็จ"); }
  };
  const markReturned = async (id) => {
    try { await http("PATCH", `/calendar-events/${id}/return`, { actual_return_date: todayISO() }); setApptModal(null); await reload(); }
    catch (e) { fail(e, "บันทึกการกลับไม่สำเร็จ"); }
  };

  const saveTube = async (t) => {
    try { await tubeSave(t); setTubeModal(null); await reloadTubes(); }
    catch (e) { fail(e, "บันทึกสายไม่สำเร็จ"); }
  };
  const tubeDone = (t) => saveTube({ ...t, lastChanged: todayISO() });
  const deleteTube = async (t) => {
    if (!window.confirm(`ลบ ${t.type}?`)) return;
    try { await tubeRemove(t.id); await reloadTubes(); } catch (e) { fail(e, "ลบสายไม่สำเร็จ"); }
  };
  const newTube = (patientId) => ({ id: "", patientId, type: TUBE_PRESETS[0].type, lastChanged: todayISO(), intervalDays: TUBE_PRESETS[0].days });

  return (
    <div className="min-h-screen bg-stone-100 text-stone-900" style={{ fontFamily: "'Noto Sans Thai','Sarabun','Segoe UI',system-ui,sans-serif" }}>
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3 px-4 py-3">
          <h1 className="text-xl font-bold">ปฏิทินคนไข้</h1>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="w-64">
              <PatientPicker patients={patients} value={patientFilter} onChange={setPatientFilter} allLabel="คนไข้ทั้งหมด" placeholder="ค้นหาห้อง/ชื่อคนไข้" />
            </div>
            <button className={`${btnGhost} relative`} aria-label="แจ้งเตือน" onClick={() => setTab("notifications")}>
              🔔{badgeCount > 0 && <span className="ml-1 rounded-full bg-red-600 px-1.5 text-xs font-medium text-white">{badgeCount}</span>}
            </button>
            <button className={btnGhost} onClick={() => { reload(); reloadTubes(); reloadSettings(); }} disabled={loading}>{loading ? "กำลังโหลด..." : "รีเฟรช"}</button>
            <button className={btnPrimary} onClick={() => setApptModal({ appt: newAppt(), isNew: true })}>+ นัดหมาย</button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-[1400px] gap-1 overflow-x-auto px-4">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm ${tab === t.key ? "border-emerald-700 font-semibold text-emerald-800" : "border-transparent text-stone-600 hover:text-stone-900"}`}>
              {t.label}{t.key === "notifications" && badgeCount > 0 && <span className="ml-1.5 rounded-full bg-red-600 px-1.5 text-xs text-white">{badgeCount}</span>}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-4">
        {error && (
          <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900" role="alert">
            <span>{error}</span>
            <button className="shrink-0 underline" onClick={() => setError("")}>ปิด</button>
          </div>
        )}
        {tubeLocal && (
          <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            ยังเรียก /api/patient-tubes ไม่ได้ ข้อมูลสายจึงเก็บไว้ในเบราว์เซอร์เครื่องนี้ชั่วคราว (ฝ่ายอื่นจะไม่เห็น) — เพิ่มไฟล์ patientTubes.js ฝั่ง backend เพื่อแชร์ข้อมูลร่วมกัน
          </div>
        )}
        {tab === "calendar" && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-stone-600">มุมมองของฝ่าย:</span>
            {["all", ...ALL_DEPTS].map((d) => (
              <button key={d} onClick={() => setDept(d)}
                className={`rounded-full border px-3 py-1 ${dept === d ? "border-emerald-700 bg-emerald-700 text-white" : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"}`}>
                {d === "all" ? "ทั้งหมด" : DEPT_LABEL[d]}
              </button>
            ))}
          </div>
        )}

        {tab === "calendar" && <MonthCalendar items={items} onAdd={(date) => setApptModal({ appt: newAppt(date), isNew: true })} onEdit={openAppt} />}
        {tab === "patient" && (
          <PatientCalendarTab patients={patients} appts={data.appts}
            patientId={calPatient || (patientFilter !== "all" ? patientFilter : "")}
            onSelect={setCalPatient}
            onAddAppt={(date, pid) => setApptModal({ appt: newAppt(date, pid), isNew: true })}
            onEditAppt={openAppt}
            onAddTube={(pid) => setTubeModal(newTube(pid))}
            onEditTube={(t) => setTubeModal(t)}
            onTubeDone={tubeDone} />
        )}
        {tab === "physio" && <PhysioPlan patients={patients} appts={data.appts} patientFilter={patientFilter} />}
        {tab === "nutrition" && <NutritionPlan patients={patients} appts={data.appts} patientFilter={patientFilter} />}
        {tab === "nursing" && <NursingPlan patients={patients} appts={data.appts} patientFilter={patientFilter} onTubeDone={tubeDone} onEdit={openAppt} />}
        {tab === "notifications" && (
          <NotificationsPanel notifs={notifs} lead={lead} settingsLocal={settingsLocal} onSaveLead={saveLead}
            acked={acked} onAck={ackKeys} myDept={myDept} setMyDept={setMyDept} onOpenAppt={openAppt} />
        )}
        {tab === "patients" && (
          <PatientsTab patients={patients} appts={data.appts}
            onViewCalendar={(pid) => { setCalPatient(pid); setTab("patient"); }}
            onAddAppt={(pid) => setApptModal({ appt: newAppt(todayISO(), pid), isNew: true })}
            onAddTube={(pid) => setTubeModal(newTube(pid))}
            onEditTube={(t) => setTubeModal(t)}
            onDeleteTube={deleteTube}
            onTubeDone={tubeDone} />
        )}
      </main>

      {apptModal && (
        <ApptForm initial={apptModal.appt} isNew={apptModal.isNew} patients={patients} onClose={() => setApptModal(null)}
          onSave={(a) => saveAppt(a, apptModal.isNew)}
          onDelete={() => deleteAppt(apptModal.appt.id)}
          onReturned={() => markReturned(apptModal.appt.id)} />
      )}
      {tubeModal && <TubeForm initial={tubeModal} onClose={() => setTubeModal(null)} onSave={saveTube} />}
    </div>
  );
}